import { connect } from "react-redux";
import PropTypes from "prop-types";
import * as React from "react";
import type { PropsWithChildren, ReactNode } from "react";
import { Button } from "react-ui";

import { nullElement, pullContext } from "../../TypingUtils";
import { ExecuteFormActionHandler, FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";
import { withFormActionHandlerExecution } from "../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { withResolvePaths } from "../../withResolvePaths";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";

import { FooterButtonOffset } from "./ModalFormButtons.styles";
import { ModalFormRemoveButton } from "./ModalFormButtons/ModalFormRemoveButton";

const mapStateToProps = (state: FullFormState) => ({
    isReadOnly: Boolean(state.settings.isReadOnly),
});

interface ModalFormCancelProps {
    children: JSX.Element;
    isReadOnly?: boolean;
}

interface ModalFormCancelContext {
    handleCancel: () => void;
}

export class ModalFormCancel extends React.PureComponent<ModalFormCancelProps> {
    public static contextTypes = {
        handleCancel: PropTypes.func,
    };

    public render(): JSX.Element {
        return (
            <FooterButtonOffset onClick={this.getContext().handleCancel}>
                {!this.props.isReadOnly ? this.props.children : <Button>Закрыть</Button>}
            </FooterButtonOffset>
        );
    }

    private getContext(): ModalFormCancelContext {
        return pullContext<ModalFormCancelContext>(this);
    }
}

export const ModalFormCancelContainer = connect(mapStateToProps)(withResolvePaths(ModalFormCancel));

type ModalFormRemoveProps = PropsWithChildren<{
    bindingPath: AbsoluteResolvedModelPath;
    children: ReactNode;
    onAfterRemove?: FormActionHandler;
    onBeforeRemove?: FormActionHandler;
    executeFormActionHandler: ExecuteFormActionHandler;
    isReadOnly?: boolean;
}>;

class ModalFormRemove extends React.PureComponent<ModalFormRemoveProps> {
    public static contextTypes = {
        handleRemove: PropTypes.func,
    };

    private async handleRemove(): Promise<void> {
        if (this.props?.onBeforeRemove != undefined) {
            const isBeforeConfirmed = await this.props.executeFormActionHandler(this.props.onBeforeRemove);
            if (isBeforeConfirmed === false) {
                return;
            }
        }
        const isConfirmed = await this.getContext().handleRemove();
        if (isConfirmed && this.props.onAfterRemove != undefined) {
            await this.props.executeFormActionHandler(this.props.onAfterRemove);
        }
    }

    public render(): ReactNode {
        return !this.props.isReadOnly ? (
            <FooterButtonOffset onClick={() => this.handleRemove()}>
                {this.props.children ?? <ModalFormRemoveButton />}
            </FooterButtonOffset>
        ) : (
            nullElement
        );
    }

    private getContext() {
        return pullContext<{ handleRemove: () => Promise<boolean> }>(this);
    }
}

export const ModalFormRemoveContainer = withResolvePaths(
    connect(mapStateToProps)(withFormActionHandlerExecution(ModalFormRemove))
);

interface ModalFormConfirmProps {
    bindingPath: AbsoluteResolvedModelPath;
    children: JSX.Element;
    onAfterConfirm?: FormActionHandler;
    onBeforeConfirm?: FormActionHandler;
    executeFormActionHandler: ExecuteFormActionHandler;
    isReadOnly?: boolean;
}

interface ModalFormConfirmContext {
    handleConfirm: () => Promise<boolean>;
}

class ModalFormConfirm extends React.PureComponent<ModalFormConfirmProps> {
    public static contextTypes = {
        handleConfirm: PropTypes.func,
    };

    private async handleConfirm(): Promise<void> {
        if (this.props?.onBeforeConfirm != undefined) {
            const isBeforeConfirmed = await this.props.executeFormActionHandler(this.props.onBeforeConfirm);

            if (isBeforeConfirmed === false) {
                return;
            }
        }
        const isConfirmed = await this.getContext().handleConfirm();
        if (isConfirmed && this.props.onAfterConfirm != undefined) {
            await this.props.executeFormActionHandler(this.props.onAfterConfirm);
        }
    }

    public render(): ReactNode {
        return !this.props.isReadOnly ? (
            <FooterButtonOffset onClick={() => this.handleConfirm()}>{this.props.children}</FooterButtonOffset>
        ) : (
            nullElement
        );
    }

    private getContext(): ModalFormConfirmContext {
        return pullContext<ModalFormConfirmContext>(this);
    }
}

export const ModalFormConfirmContainer = withResolvePaths(
    connect(mapStateToProps)(withFormActionHandlerExecution(ModalFormConfirm))
);
